// CODICE src/models/mappers/earthquake/earthquake-mapper.ts

import { type EarthquakePaginationDTO } from '@/models/dto/earthquake/earthquake-pagination-dto'
import {
    type EarthquakeDTO,
    type NuovoEarthquakeDTO,
    type AnalisiSismaDTO,
} from '@/models/dto/earthquake/earthquake-dto'
import { mapToPagination } from '@/models/mappers/pagination-mapper'
import { type Earthquake, type NewEarthquake } from '@/models/types/earthquake/earthquake'
import { type EarthquakeWithPagination } from '@/models/types/earthquake/earthquake-with-pagination'
import { type AnalisiSisma } from '@/models/types/earthquake/analisi-sisma'
import { parseUtc } from '@/lib/parse-utc'

/**
 * Mappa un singolo `EarthquakeDTO` a un modello `Earthquake` (frontend).
 */
function mapToAnalisiSisma(analisiDto: AnalisiSismaDTO): AnalisiSisma {
    return {
        id: analisiDto.id,
        maxGH: analisiDto.maxGH,
        maxGV: analisiDto.maxGV,
        maxSH: analisiDto.maxSH,
        maxSV: analisiDto.maxSV,
        sogliaAlertH: analisiDto.sogliaAlertH,
        sogliaAlertV: analisiDto.sogliaAlertV,
        sogliaSloH: analisiDto.sogliaSloH,
        sogliaSloV: analisiDto.sogliaSloV,
        sogliaSldH: analisiDto.sogliaSldH,
        sogliaSldV: analisiDto.sogliaSldV,
        sogliaSlvH: analisiDto.sogliaSlvH,
        sogliaSlvV: analisiDto.sogliaSlvV,
        sogliaSlcH: analisiDto.sogliaSlcH,
        sogliaSlcV: analisiDto.sogliaSlcV,
        qtyAlertH: analisiDto.qtyAlertH,
        qtyAlertV: analisiDto.qtyAlertV,
        qtyAlertSloH: analisiDto.qtyAlertSloH,
        qtyAlertSloV: analisiDto.qtyAlertSloV,
        qtySloSldH: analisiDto.qtySloSldH,
        qtySloSldV: analisiDto.qtySloSldV,
        qtySldSlvH: analisiDto.qtySldSlvH,
        qtySldSlvV: analisiDto.qtySldSlvV,
        qtySlvSlcH: analisiDto.qtySlvSlcH,
        qtySlvSlcV: analisiDto.qtySlvSlcV,
        qtySlcMaxH: analisiDto.qtySlcMaxH,
        qtySlcMaxV: analisiDto.qtySlcMaxV,
        idSisma: analisiDto.idSisma,
        idSensoreDinamico: analisiDto.idSensoreDinamico,
        idConfigSisma: analisiDto.idConfigSisma,
        configAnalisiSisma: analisiDto.configAnalisiSisma
            ? {
                  id: analisiDto.configAnalisiSisma.id,
                  nZero: analisiDto.configAnalisiSisma.nZero,
                  mfreqtaglioL: analisiDto.configAnalisiSisma.mfreqtaglioL,
                  mfreqtaglioH: analisiDto.configAnalisiSisma.mfreqtaglioH,
                  morder: analisiDto.configAnalisiSisma.morder,
                  mptseg: analisiDto.configAnalisiSisma.mptseg,
                  filtro: analisiDto.configAnalisiSisma.filtro,
                  dt: analisiDto.configAnalisiSisma.dt,
                  ndata: analisiDto.configAnalisiSisma.ndata,
                  ncanali: analisiDto.configAnalisiSisma.ncanali,
                  versione: analisiDto.configAnalisiSisma.versione,
              }
            : undefined,
    }
}

export function mapToEarthquake(dto: EarthquakeDTO): Earthquake {
    return {
        id: dto.id,
        dataInizioSisma: parseUtc(dto.dataInizioSisma),
        dataFineSisma: parseUtc(dto.dataFineSisma),
        esitoAnalisi: dto.esitoAnalisi,
        colonneSuperamentoSoglia: dto.colonneSuperamentoSoglia,
        idPLC: dto.idPLC,
        nomePLC: dto.nomePLC,
        idImpianto: dto.idImpianto,
        nomeImpianto: dto.nomeImpianto,
        iconUrl: dto.iconUrl,
        plcAlias: dto.plcAlias,
        // Converte l'array di dto in un array di AnalisiSisma
        analisiSisma: dto.analisiSisma.map(mapToAnalisiSisma),
        nota: dto.nota
    }
}
/**
 * Mappa un array di `EarthquakeDTO` a un array di `Earthquake`.
 */
export function mapToEarthquakes(dtos: EarthquakeDTO[]): Earthquake[] {
    return dtos.map((dto) => mapToEarthquake(dto))
}

/**
 * Mappa un `EarthquakePaginationDTO` (ritornato dalle API)
 * a un `EarthquakeWithPagination` utilizzabile nel frontend.
 */
export function mapToEarthquakesWithPagination(dto: EarthquakePaginationDTO): EarthquakeWithPagination {
    return {
        // Qui usi dto.sisma invece di dto.earthquakes
        earthquakes: dto.sisma ? mapToEarthquakes(dto.sisma) : [],
        pagination: dto.paginationData ? mapToPagination(dto.paginationData) : undefined,
    }
}

/**
 * Mappa un `NewEarthquake` del frontend (creazione sisma)
 * a un `NuovoEarthquakeDTO` da inviare in POST alle API.
 */
export function mapToNuovoEarthquakeDTO(e: NewEarthquake): NuovoEarthquakeDTO {
    return {
        dataInizioSisma: e.dataInizioSisma.toISOString(),
        dataFineSisma: e.dataFineSisma.toISOString(),
        esitoAnalisi: e.esitoAnalisi,
        colonneSuperamentoSoglia: e.colonneSuperamentoSoglia,
        idPLC: e.idPLC,
        nomePLC: e.nomePLC,
        idImpianto: e.idImpianto,
        nomeImpianto: e.nomeImpianto,
        iconUrl: e.iconUrl,
        plcAlias : e.plcAlias,
        analisiSisma: e.analisiSisma,
    }
}

/**
 * Mappa un `Earthquake` del frontend (update sisma)
 * a un `EarthquakeDTO` (incluso l'ID) da inviare in PUT alle API.
 */
export function mapToEarthquakeDTO(e: Earthquake): EarthquakeDTO {
    return {
        id: e.id,
        dataInizioSisma: e.dataInizioSisma.toISOString(),
        dataFineSisma: e.dataFineSisma.toISOString(),
        esitoAnalisi: e.esitoAnalisi,
        colonneSuperamentoSoglia: e.colonneSuperamentoSoglia,
        idPLC: e.idPLC,
        nomePLC: e.nomePLC,
        idImpianto: e.idImpianto,
        nomeImpianto: e.nomeImpianto,
        iconUrl: e.iconUrl,
        plcAlias : e.plcAlias,
        analisiSisma: e.analisiSisma,
    }
}
